import React, { useState, useRef, useCallback } from "react";
import {
  Box,
  TextField,
  IconButton,
  Tooltip,
  CircularProgress,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
} from "@mui/material";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import FolderOpenIcon from "@mui/icons-material/FolderOpen";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import StopIcon from "@mui/icons-material/Stop";
import ClearIcon from "@mui/icons-material/Clear";
import MusicNoteIcon from "@mui/icons-material/MusicNote";
import entityMediaApi from "./entityMediaApi";
import MediaCenterBrowser from "./MediaCenterBrowser";

const RINGBACK_TONES = [
  { label: "None", value: "none" },
  { label: "US Ring", value: "${us-ring}" },
  { label: "UK Ring", value: "${uk-ring}" },
];

export default function MediaFileControl({
  label,
  value,
  onChange,
  entityType,
  fieldName,
  size = "small",
  disabled = false,
  showMediaCenter = true,
  showRingbackTones = false,
  apiAdapter,
}) {
  const [uploading, setUploading] = useState(false);
  const [playing, setPlaying] = useState(false);
  const [browserOpen, setBrowserOpen] = useState(false);
  const [toneMenuAnchor, setToneMenuAnchor] = useState(null);
  const fileInputRef = useRef(null);
  const audioRef = useRef(null);
  const api = apiAdapter || entityMediaApi;

  const isFilePath = value && value.startsWith("/");
  const displayName = value ? (isFilePath ? value.split("/").pop() : value) : "";

  const handleUpload = useCallback(
    async (event) => {
      const file = event.target.files[0];
      if (!file) return;

      setUploading(true);
      try {
        const result = await api.upload(file, entityType, fieldName);
        if (result.success && result.data) {
          onChange(result.data.filePath);
        }
      } catch (err) {
        // Error toast handled by Axios interceptor
      } finally {
        setUploading(false);
        if (fileInputRef.current) fileInputRef.current.value = "";
      }
    },
    [api, entityType, fieldName, onChange]
  );

  const handlePlay = useCallback(async () => {
    if (playing) {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
      setPlaying(false);
      return;
    }

    if (!value || !isFilePath) return;

    try {
      const blob = await api.fetchAudioBlob(value);
      const url = URL.createObjectURL(blob);
      const audio = new Audio(url);
      audioRef.current = audio;
      setPlaying(true);

      audio.onended = () => {
        setPlaying(false);
        URL.revokeObjectURL(url);
        audioRef.current = null;
      };
      audio.onerror = () => {
        setPlaying(false);
        URL.revokeObjectURL(url);
        audioRef.current = null;
      };

      audio.play();
    } catch (err) {
      setPlaying(false);
    }
  }, [api, value, playing, isFilePath]);

  const handleClear = useCallback(() => {
    if (playing && audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
      setPlaying(false);
    }
    onChange("");
  }, [onChange, playing]);

  const handleBrowserSelect = useCallback(
    (path) => {
      onChange(path);
      setBrowserOpen(false);
    },
    [onChange]
  );

  const handleToneSelect = useCallback(
    (toneValue) => {
      onChange(toneValue);
      setToneMenuAnchor(null);
    },
    [onChange]
  );

  return (
    <>
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
        <TextField
          label={label}
          value={displayName}
          size={size}
          disabled={disabled}
          fullWidth
          InputProps={{ readOnly: true }}
        />

        <input
          type="file"
          accept=".wav"
          ref={fileInputRef}
          style={{ display: "none" }}
          onChange={handleUpload}
        />

        <Tooltip title="Upload WAV file">
          <span>
            <IconButton
              size={size}
              disabled={disabled || uploading}
              onClick={() => fileInputRef.current?.click()}
            >
              {uploading ? (
                <CircularProgress size={20} />
              ) : (
                <UploadFileIcon fontSize="small" />
              )}
            </IconButton>
          </span>
        </Tooltip>

        {showMediaCenter && (
          <Tooltip title="Select from Media Center">
            <span>
              <IconButton
                size={size}
                disabled={disabled}
                onClick={() => setBrowserOpen(true)}
              >
                <FolderOpenIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        )}

        {showRingbackTones && (
          <Tooltip title="Ringback Tones">
            <span>
              <IconButton
                size={size}
                disabled={disabled}
                onClick={(e) => setToneMenuAnchor(e.currentTarget)}
              >
                <MusicNoteIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        )}

        <Tooltip title={playing ? "Stop" : "Play"}>
          <span>
            <IconButton
              size={size}
              disabled={disabled || !value || !isFilePath}
              onClick={handlePlay}
            >
              {playing ? (
                <StopIcon fontSize="small" />
              ) : (
                <PlayArrowIcon fontSize="small" />
              )}
            </IconButton>
          </span>
        </Tooltip>

        <Tooltip title="Clear">
          <span>
            <IconButton
              size={size}
              disabled={disabled || !value}
              onClick={handleClear}
            >
              <ClearIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      </Box>

      {showMediaCenter && (
        <MediaCenterBrowser
          open={browserOpen}
          onClose={() => setBrowserOpen(false)}
          onSelect={handleBrowserSelect}
          apiAdapter={api}
        />
      )}

      {showRingbackTones && (
        <Menu
          anchorEl={toneMenuAnchor}
          open={Boolean(toneMenuAnchor)}
          onClose={() => setToneMenuAnchor(null)}
        >
          {RINGBACK_TONES.map((tone) => (
            <MenuItem
              key={tone.value}
              selected={value === tone.value}
              onClick={() => handleToneSelect(tone.value)}
            >
              <ListItemIcon>
                <MusicNoteIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText primary={tone.label} secondary={tone.value} />
            </MenuItem>
          ))}
        </Menu>
      )}
    </>
  );
}
